import type { SetLanguage } from '../constants';
import type { CollectionLot } from '../types';

/** Condition codes stored on lots. Absent means unspecified. */
export type LotCondition = 'M' | 'NM' | 'LP' | 'MP' | 'HP' | 'D' | 'S';

export const LOT_CONDITIONS: readonly LotCondition[] = ['M', 'NM', 'LP', 'MP', 'HP', 'D', 'S'];

export const LOT_CONDITION_LABELS: Record<LotCondition, string> = {
  M: 'Mint',
  NM: 'Near Mint',
  LP: 'Lightly Played',
  MP: 'Moderately Played',
  HP: 'Heavily Played',
  D: 'Damaged',
  S: 'Sealed',
};

export const LOT_GRADING_COMPANIES = [
  'PSA',
  'CGC',
  'BGS',
  'AP',
  'Ace',
  'GMA',
  'HGA',
  'PCA',
  'SGC',
  'TAG',
  'PCG',
  'Other',
] as const;

export type LotGradingCompany = (typeof LOT_GRADING_COMPANIES)[number];

/** Compact Firestore lot row. Defaults are omitted to keep the bag small. */
export type SerializedCollectionLot = {
  q: number;
  l?: Exclude<SetLanguage, 'en'>;
  c?: LotCondition;
  f?: 1;
  g?: [company: string, grade: string];
  n?: string;
};

const LANGUAGE_ALIASES: Record<string, SetLanguage> = {
  en: 'en',
  eng: 'en',
  english: 'en',
  zh: 'zh',
  zho: 'zh',
  chi: 'zh',
  chinese: 'zh',
  cn: 'zh',
  'zh-cn': 'zh',
  'zh-hans': 'zh',
  ko: 'ko',
  kor: 'ko',
  korean: 'ko',
  kr: 'ko',
  fr: 'fr',
  fra: 'fr',
  fre: 'fr',
  french: 'fr',
};

const CONDITION_ALIASES: Record<string, LotCondition> = {
  m: 'M',
  mint: 'M',
  nm: 'NM',
  nearmint: 'NM',
  'near mint': 'NM',
  lp: 'LP',
  lightlyplayed: 'LP',
  'lightly played': 'LP',
  mp: 'MP',
  moderatelyplayed: 'MP',
  'moderately played': 'MP',
  hp: 'HP',
  heavilyplayed: 'HP',
  'heavily played': 'HP',
  d: 'D',
  dmg: 'D',
  damaged: 'D',
  s: 'S',
  sealed: 'S',
};

export function parseLotLanguage(value: string | undefined): SetLanguage | undefined {
  const key = (value ?? '').trim().toLowerCase();
  if (!key) return undefined;
  return LANGUAGE_ALIASES[key] ?? LANGUAGE_ALIASES[key.replace(/[^a-z]/g, '')];
}

export function parseLotCondition(value: string | undefined): LotCondition | undefined {
  const raw = (value ?? '').trim();
  if (!raw) return undefined;
  const key = raw.toLowerCase();
  return CONDITION_ALIASES[key] ?? CONDITION_ALIASES[key.replace(/[^a-z]/g, '')];
}

export function lotLanguageLabel(code: SetLanguage | undefined): string {
  if (!code || code === 'en') return 'English';
  if (code === 'zh') return 'Chinese';
  if (code === 'ko') return 'Korean';
  if (code === 'fr') return 'French';
  return code;
}

function isLotCondition(value: unknown): value is LotCondition {
  return typeof value === 'string' && (LOT_CONDITIONS as readonly string[]).includes(value);
}

function isSetLanguage(value: unknown): value is SetLanguage {
  return value === 'en' || value === 'zh' || value === 'ko' || value === 'fr';
}

export function emptyLots(): CollectionLot[] {
  return [];
}

export function lotKey(lot: Pick<CollectionLot, 'language' | 'condition' | 'foil' | 'grading' | 'notes'>): string {
  const grade = lot.grading ? `${lot.grading.company}\0${lot.grading.grade}` : '';
  return [
    lot.language ?? 'en',
    lot.condition ?? '',
    lot.foil ? '1' : '0',
    grade,
    lot.notes ?? '',
  ].join('|');
}

export function mergeLots(
  a: CollectionLot[] | undefined,
  b: CollectionLot[] | undefined,
): CollectionLot[] | undefined {
  const all = [...(a ?? []), ...(b ?? [])].filter((lot) => lot.quantity > 0);
  if (!all.length) return undefined;
  const byKey = new Map<string, CollectionLot>();
  for (const lot of all) {
    const key = lotKey(lot);
    const prev = byKey.get(key);
    if (prev) {
      prev.quantity += lot.quantity;
    } else {
      byKey.set(key, { ...lot, grading: lot.grading ? { ...lot.grading } : undefined });
    }
  }
  return [...byKey.values()];
}

/**
 * Add or remove copies of one printed language. English stays unallocated
 * (no lot). A foil flip passes a zero quantity change and a foil delta from
 * the caller via {@link applyPrintedLanguageDelta}.
 */
export function adjustLanguageCopies(
  lots: CollectionLot[] | undefined,
  language: SetLanguage,
  delta: number,
  foil: boolean,
): CollectionLot[] | undefined {
  if (language === 'en' || delta === 0) return lots;
  const next = (lots ?? []).map((lot) => ({ ...lot }));
  const index = next.findIndex((lot) => (lot.language ?? 'en') === language && Boolean(lot.foil) === foil);
  if (delta > 0) {
    if (index >= 0) next[index] = { ...next[index], quantity: next[index].quantity + delta };
    else next.push({ quantity: delta, language, ...(foil ? { foil: true } : {}) });
    return next;
  }
  if (index < 0) return next.length ? next : undefined;
  const quantity = next[index].quantity + delta;
  if (quantity <= 0) next.splice(index, 1);
  else next[index] = { ...next[index], quantity };
  return next.length ? next : undefined;
}

/**
 * Apply a scan quantity change to the lot for `language`.
 * A foil-only delta (quantity unchanged) moves copies between foil and non-foil
 * for that language. English does not create a lot.
 */
export function applyPrintedLanguageDelta(
  lots: CollectionLot[] | undefined,
  language: SetLanguage | undefined,
  quantityDelta: number,
  foilDelta: number,
): CollectionLot[] | undefined {
  if (!language || language === 'en') return lots;
  if (quantityDelta === 0 && foilDelta !== 0) {
    const toFoil = foilDelta > 0;
    const amount = Math.abs(foilDelta);
    const removed = adjustLanguageCopies(lots, language, -amount, !toFoil);
    return adjustLanguageCopies(removed, language, amount, toFoil);
  }
  if (quantityDelta === 0) return lots;
  return adjustLanguageCopies(lots, language, quantityDelta, foilDelta !== 0);
}

export function rollupFromLots(lots: CollectionLot[] | undefined): {
  quantity: number;
  quantityFoil: number;
} {
  let quantity = 0;
  let quantityFoil = 0;
  for (const lot of lots ?? []) {
    const q = Math.max(0, Math.floor(lot.quantity) || 0);
    quantity += q;
    if (lot.foil) quantityFoil += q;
  }
  return { quantity, quantityFoil };
}

/** Drop empty lots and trim from the end until they fit the rollup. */
export function clampLotsToQuantities(
  lots: CollectionLot[] | undefined,
  quantity: number,
  quantityFoil: number,
): CollectionLot[] | undefined {
  if (!lots?.length) return undefined;
  const qCap = Math.max(0, Math.floor(quantity) || 0);
  const fCap = Math.min(Math.max(0, Math.floor(quantityFoil) || 0), qCap);
  if (qCap <= 0) return undefined;

  const next: CollectionLot[] = [];
  let used = 0;
  let usedFoil = 0;
  const normalize = (lot: CollectionLot, quantity: number, foil: boolean): CollectionLot => ({
    quantity,
    language: lot.language && lot.language !== 'en' ? lot.language : undefined,
    condition: lot.condition,
    foil: foil || undefined,
    grading: lot.grading?.company && lot.grading.grade
      ? { company: lot.grading.company, grade: lot.grading.grade }
      : undefined,
    notes: lot.notes?.trim() || undefined,
  });
  for (const lot of lots) {
    let q = Math.max(0, Math.floor(lot.quantity) || 0);
    if (!q) continue;
    const remaining = qCap - used;
    if (remaining <= 0) break;
    q = Math.min(q, remaining);
    let foil = Boolean(lot.foil);
    if (foil) {
      const foilRoom = fCap - usedFoil;
      if (foilRoom <= 0) foil = false;
      else if (q > foilRoom) {
        // Split: keep as much foil as the rollup allows, remainder as non-foil.
        // Both halves normalize the same way, or they stop sharing a lot key.
        next.push(normalize(lot, foilRoom, true));
        used += foilRoom;
        usedFoil += foilRoom;
        q -= foilRoom;
        foil = false;
        if (!q) continue;
      }
    }
    next.push(normalize(lot, q, foil));
    used += q;
    if (foil) usedFoil += q;
  }
  return next.length ? next : undefined;
}

export function assertLotInvariants(
  quantity: number,
  quantityFoil: number,
  lots: CollectionLot[] | undefined,
): void {
  if (!lots?.length) return;
  const rollup = rollupFromLots(lots);
  if (rollup.quantity > quantity) {
    throw new Error('Lot quantities exceed owned copies');
  }
  if (rollup.quantityFoil > quantityFoil) {
    throw new Error('Foil lots exceed owned foil copies');
  }
}

export function serializeLots(lots: CollectionLot[] | undefined): SerializedCollectionLot[] | undefined {
  if (!lots?.length) return undefined;
  const rows: SerializedCollectionLot[] = [];
  for (const lot of lots) {
    const q = Math.max(0, Math.floor(lot.quantity) || 0);
    if (!q) continue;
    const row: SerializedCollectionLot = { q };
    if (lot.language && lot.language !== 'en') row.l = lot.language;
    if (lot.condition) row.c = lot.condition;
    if (lot.foil) row.f = 1;
    if (lot.grading?.company && lot.grading.grade) {
      row.g = [lot.grading.company, lot.grading.grade];
    }
    if (lot.notes?.trim()) row.n = lot.notes.trim();
    rows.push(row);
  }
  return rows.length ? rows : undefined;
}

export function parseLots(
  raw: unknown,
  quantity: number,
  quantityFoil: number,
): CollectionLot[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const lots: CollectionLot[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const q = Math.max(0, Math.floor(Number(rec.q) || 0));
    if (!q) continue;
    const language = isSetLanguage(rec.l) && rec.l !== 'en' ? rec.l : undefined;
    const condition = isLotCondition(rec.c) ? rec.c : undefined;
    const foil = rec.f === 1 || rec.f === true;
    let grading: CollectionLot['grading'];
    if (Array.isArray(rec.g) && rec.g.length >= 2) {
      const company = String(rec.g[0] ?? '').trim();
      const grade = String(rec.g[1] ?? '').trim();
      if (company && grade) grading = { company, grade };
    }
    const notes = typeof rec.n === 'string' && rec.n.trim() ? rec.n.trim() : undefined;
    lots.push({
      quantity: q,
      language,
      condition,
      foil: foil || undefined,
      grading,
      notes,
    });
  }
  const clamped = clampLotsToQuantities(lots, quantity, quantityFoil);
  return clamped;
}

export function lotsHaveDetails(lots: CollectionLot[] | undefined): boolean {
  return Boolean(lots?.some((lot) => (
    (lot.language && lot.language !== 'en')
    || lot.condition
    || lot.grading
    || lot.notes
    || lot.foil
  )));
}

/** Compact chips for a tile: dominant language/condition, or Mixed. */
export function lotsChipParts(lots: CollectionLot[] | undefined): string[] {
  if (!lots?.length) return [];
  const langs = new Set(
    lots
      .filter((lot) => lot.language && lot.language !== 'en')
      .map((lot) => (lot.language as SetLanguage).toUpperCase()),
  );
  const conds = new Set(
    lots.map((lot) => lot.condition).filter((c): c is LotCondition => Boolean(c)),
  );
  const graded = lots.some((lot) => lot.grading);
  if (!langs.size && !conds.size && !graded) return [];
  if (langs.size > 1 || conds.size > 1) return ['Mixed'];
  const parts: string[] = [];
  const lang = [...langs][0];
  const cond = [...conds][0];
  if (lang) parts.push(lang);
  if (cond) parts.push(LOT_CONDITION_LABELS[cond]);
  if (graded && !parts.length) parts.push('Graded');
  return parts;
}

export function lotsChipLabel(lots: CollectionLot[] | undefined): string | null {
  const parts = lotsChipParts(lots);
  return parts.length ? parts.join(' · ') : null;
}

export function parseLotFromCsvRow(row: {
  language?: string;
  condition?: string;
  gradingCompany?: string;
  gradingValue?: string;
  gradingLabel?: string;
}): Omit<CollectionLot, 'quantity'> | null {
  const language = parseLotLanguage(row.language);
  const condition = parseLotCondition(row.condition);
  const company = (row.gradingCompany ?? '').trim();
  // Both columns are always present (blank) on our own export, so fall back on
  // emptiness rather than on nullishness.
  const grade = (row.gradingValue || row.gradingLabel || '').trim();
  const grading = company && grade ? { company, grade } : undefined;
  const hasLanguage = language && language !== 'en';
  if (!hasLanguage && !condition && !grading) return null;
  return {
    language: hasLanguage ? language : undefined,
    condition,
    grading,
  };
}

export function collectionLotToCsvFields(lot: CollectionLot | undefined): {
  language: string;
  condition: string;
  gradingCompany: string;
  gradingValue: string;
  gradingLabel: string;
} {
  return {
    language: lotLanguageLabel(lot?.language),
    condition: lot?.condition ? LOT_CONDITION_LABELS[lot.condition] : '',
    gradingCompany: lot?.grading?.company ?? '',
    gradingValue: lot?.grading?.grade ?? '',
    gradingLabel: '',
  };
}
